import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { Subscription } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';

import { Alert, AlertType, AlertService } from '../../_service/alert.service';

@Component({
    standalone: true,
    imports: [ToastModule],
    selector: 'alert',
    template: `<p-toast 
        [position]="position" 
        [key]="id"
        [preventOpenDuplicates]="preventDuplicates"
        [preventDuplicates]="preventDuplicates"
        [showTransformOptions]="showTransformOptions"
        [hideTransformOptions]="hideTransformOptions"
        [showTransitionOptions]="showTransitionOptions"
        [hideTransitionOptions]="hideTransitionOptions">
    </p-toast>`,
    //providers: [MessageService]
})
export class AlertComponent implements OnInit, OnDestroy {
    @Input() id = 'default-alert';
    @Input() position: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right' | 'center' = 'top-center';
    @Input() preventDuplicates = true;
    @Input() showTransformOptions = 'translateY(100%)';
    @Input() hideTransformOptions = 'translateY(-100%)';
    @Input() showTransitionOptions = '300ms ease-out';
    @Input() hideTransitionOptions = '250ms ease-in';

    private alertSubscription!: Subscription;
    private routeSubscription!: Subscription;

    constructor(
        private router: Router,
        private alertService: AlertService,
        private messageService: MessageService
    ) { }

    ngOnInit() {
        // Subscribe to new alert notifications
        this.alertSubscription = this.alertService.onAlert(this.id)
            .subscribe(alert => {
                // Clear alerts when an empty alert is received
                if (!alert.message) {
                    this.messageService.clear(this.id);
                    return;
                }

                // Add alert to PrimeNG Toast
                this.messageService.add({
                    key: this.id,
                    severity: this.mapAlertTypeToSeverity(alert.type),
                    summary: this.getAlertSummary(alert.type),
                    detail: alert.message,
                    life: alert.life ?? (alert.autoClose ? 3000 : 0),
                    closable: true,
                    data: {
                        keepAfterRouteChange: alert.keepAfterRouteChange,
                        originalAlert: alert
                    }
                });
            });

        // Clear alerts on location change (except those marked to keep)
        this.routeSubscription = this.router.events.subscribe(event => {
            if (event instanceof NavigationStart) {
                // Get current messages and filter out those that should be kept
                const currentMessages = this.alertService.getActiveAlertsForId(this.id);
                const messagesToKeep = currentMessages.filter((msg: any) =>
                    msg.keepAfterRouteChange
                );

                // Clear all messages for this key
                this.messageService.clear(this.id);

                // Re-add messages that should be kept (without the keepAfterRouteChange flag)
                messagesToKeep.forEach((msg: any) => {
                    this.messageService.add({
                        key: this.id,
                        severity: this.mapAlertTypeToSeverity(msg.type),
                        summary: this.getAlertSummary(msg.type),
                        detail: msg.message,
                        life: msg.autoClose ? 5000 : 0,
                        closable: true,
                        data: {
                            keepAfterRouteChange: false,
                            originalAlert: msg
                        }
                    });
                });
            }
        });
    }

    ngOnDestroy() {
        // Unsubscribe to avoid memory leaks
        this.alertSubscription?.unsubscribe();
        this.routeSubscription?.unsubscribe();

        // Clear any remaining messages for this instance
        this.messageService.clear(this.id);
    }

    private mapAlertTypeToSeverity(alertType: AlertType): 'success' | 'info' | 'warn' | 'error' {
        switch (alertType) {
            case AlertType.Success:
                return 'success';
            case AlertType.Error:
                return 'error';
            case AlertType.Warning:
                return 'warn';
            case AlertType.Info:
            default:
                return 'info';
        }
    }

    private getAlertSummary(alertType: AlertType): string {
        switch (alertType) {
            case AlertType.Success:
                return 'Success';
            case AlertType.Error:
                return 'Error';
            case AlertType.Warning:
                return 'Warning';
            case AlertType.Info:
            default:
                return 'Information';
        }
    }
}
